import { useEffect, useState } from "react";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const pad = (n: number) => String(n).padStart(2, "0");

const Cell = ({ value, label }: { value: string; label: string }) => (
  <div className="flex flex-col items-center">
    <span className="font-mono text-2xl font-bold tabular-nums leading-none sm:text-3xl">{value}</span>
    <span className="mt-1 text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</span>
  </div>
);

/**
 * Time left until registration closes.
 *
 * Ticks once a minute: the smallest unit shown is a minute, so a per-second interval would cost a
 * re-render a second and change nothing on screen. Renders nothing once the moment has passed, so
 * callers never need a guard of their own - and inherits colour from its parent, because it sits on
 * the brand-green hero in one place and on a muted card in another.
 */
export const RegistrationCountdown = ({
  closesAt,
  label,
  onExpire,
  className = "",
}: {
  closesAt: string;
  /** Rendered above the digits, and only while they are - a heading with nothing under it reads as broken. */
  label?: string;
  /**
   * Fired once when the moment passes. Only this component has a timer, so a page that decides
   * anything from the deadline has to hear about it from here - otherwise someone who left the tab
   * open watches the countdown hit zero and can still fill in a form the server will refuse.
   */
  onExpire?: () => void;
  className?: string;
}) => {
  const target = new Date(closesAt).getTime();
  const [remaining, setRemaining] = useState(() => target - Date.now());

  useEffect(() => {
    setRemaining(target - Date.now());
    const tick = setInterval(() => setRemaining(target - Date.now()), MINUTE);
    // The interval alone only notices the deadline on its next minute boundary, so a page opened
    // forty seconds before closing would keep offering a form the server has already shut. This
    // fires at the moment itself.
    const left = target - Date.now();
    const atDeadline = left > 0 && Number.isFinite(left)
      ? setTimeout(() => setRemaining(target - Date.now()), left + 250)
      : undefined;
    return () => {
      clearInterval(tick);
      if (atDeadline !== undefined) clearTimeout(atDeadline);
    };
  }, [target]);

  const expired = !Number.isFinite(target) || remaining <= 0;
  useEffect(() => {
    if (expired) onExpire?.();
  }, [expired, onExpire]);

  if (expired) return null;

  const days = Math.floor(remaining / DAY);
  const hours = Math.floor((remaining % DAY) / HOUR);
  const minutes = Math.floor((remaining % HOUR) / MINUTE);

  return (
    <div className={className}>
      {label && (
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] opacity-60">{label}</p>
      )}
      <div className="mt-2 flex items-start justify-center gap-4 sm:gap-6">
      {days > 0 && <Cell value={String(days)} label={days === 1 ? "day" : "days"} />}
      <Cell value={pad(hours)} label={hours === 1 ? "hour" : "hours"} />
      <Cell value={pad(minutes)} label={minutes === 1 ? "min" : "mins"} />
      </div>
    </div>
  );
};
